import {
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectIsAuthenticated, selectRole } from "../../features/authSlice";
import { forgotPassword, LoginUser } from "../../utils/Apis";

const LoginSignup = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [forgotMsg, setForgotMsg] = useState(null); // <-- for success message
  const [loading, setLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false); // <-- forgot loading

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const role = useSelector(selectRole);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
    setForgotMsg(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setForgotMsg(null);

    try {
      const resultAction = await dispatch(LoginUser(formData));
      if (LoginUser.fulfilled.match(resultAction)) {
        const { role } = resultAction.payload;
        if (role === "student") navigate("/admin-dashboard");
        if (role === "admin") navigate("/admin-dashboard");
        if (role === "staff") navigate("/staff-dashboard");
      } else {
        setError(resultAction.payload || "Login failed. Please try again.");
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError(null);
    setForgotMsg(null);

    if (!formData.email) {
      setError("Enter an email for forgot password");
      return;
    }

    try {
      setForgotLoading(true);
      const result = await dispatch(forgotPassword(formData.email));
      if (forgotPassword.fulfilled.match(result)) {
        setForgotMsg(result.payload.msg); // "Reset link sent to email"
      } else {
        setError(result.payload || "Failed to send reset link");
      }
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setForgotLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      switch (role) {
        case "admin":
          navigate("/admin-dashboard");
          break;
        case "student":
          navigate("/student-dashboard");
          break;
        case "staff":
          navigate("/staff-dashboard");
          break;
        default:
          navigate("/");
      }
    }
  }, [isAuthenticated, role, navigate]);

  return (
    <Box
      sx={{
        p: 4,
        backgroundColor: "#f5f5f5",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: 2,
          backgroundColor: "white",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          borderRadius: "16px",
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "#333", mb: 2 }}
        >
          Welcome Back
        </Typography>
        <Typography sx={{ textAlign: "center", mb: 4 }}>
          Login to continue your journey.
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "300px",
          }}
        >
          <TextField
            label="Email"
            name="email"
            variant="outlined"
            fullWidth
            value={formData.email}
            onChange={handleInputChange}
            required
          />
          <TextField
            label="Password"
            name="password"
            variant="outlined"
            type="password"
            fullWidth
            value={formData.password}
            onChange={handleInputChange}
            required
          />
          <Button
            type="submit"
            variant="contained"
            sx={{
              bgcolor: "black",
              "&:hover": { bgcolor: "grey.800" },
              borderRadius: "22px",
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Login"}
          </Button>

          {error && <Typography color="error">{error}</Typography>}
          {forgotMsg && <Typography color="primary">{forgotMsg}</Typography>}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            sx={{
              borderColor: "black",
              color: "black",
              width: "300px",
              textTransform: "none",
              borderRadius: "22px",
            }}
            onClick={handleForgotPassword}
            disabled={forgotLoading}
          >
            {forgotLoading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Forgot Password?"
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginSignup;
